/**
 * BackendControl — pick the LLM model backend (provider) for the signed-in user.
 *
 * A "backend" is a provider (anthropic / openai / … / openrouter); choosing one
 * sets the caller's own provider for every task via `PUT /user-config/me`. Each
 * row previews the backend's small + big model so the choice is legible. The
 * change is per-user and persists; new runs/chats pick it up automatically
 * (they resolve the provider per request).
 *
 * Self-contained: fetches its own state via api/client (getBackends/setBackend)
 * and reuses the TopBarC inline-dropdown pattern (open state + click-outside).
 * Ported from guru-web/src/BackendControl.tsx, restyled for the add-in.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { type BackendRow, type BackendsState, getBackends, setBackend } from "../../api/client";
import { brandColors, fonts, inkColors, surfaceColors } from "../tokens";

/** One line summarising a backend's small + big model. */
function modelSummary(row: BackendRow): string {
  const bits: string[] = [];
  if (row.small) bits.push(`small: ${row.small.model}`);
  if (row.big) bits.push(`big: ${row.big.model}`);
  return bits.join(" · ");
}

const useStyles = makeStyles({
  root: {
    position: "relative",
    flexShrink: 0,
  },
  trigger: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "0.4px",
    fontFamily: fonts.body,
    textTransform: "uppercase",
    color: inkColors.ink3,
    background: surfaceColors.paperDim,
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "6px",
    padding: "4px 8px",
    cursor: "pointer",
    lineHeight: 1,
    maxWidth: "140px",
    ":hover": {
      color: brandColors.primary,
      border: `1px solid ${brandColors.primary}`,
    },
    ":focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "1px",
    },
  },
  triggerIcon: {
    fontSize: "11px",
    opacity: 0.85,
  },
  triggerLabel: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  popover: {
    position: "absolute",
    top: "100%",
    left: "0",
    marginTop: "4px",
    background: "white",
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.12)",
    zIndex: 1000,
    minWidth: "240px",
    maxWidth: "300px",
    maxHeight: "320px",
    overflowY: "auto",
    padding: "6px 0",
  },
  head: {
    padding: "4px 12px 6px",
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    color: inkColors.ink4,
    borderBottom: `1px solid ${surfaceColors.line}`,
  },
  note: {
    padding: "8px 12px",
    fontSize: "11px",
    fontFamily: fonts.body,
    color: inkColors.ink4,
  },
  error: {
    padding: "8px 12px",
    fontSize: "11px",
    fontFamily: fonts.body,
    color: "#B42318",
  },
  row: {
    width: "100%",
    textAlign: "left",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    padding: "8px 12px",
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    ":hover": {
      background: surfaceColors.paperDim,
    },
    ":disabled": {
      cursor: "wait",
      opacity: 0.6,
    },
  },
  rowSelected: {
    background: surfaceColors.paperDim,
  },
  rowHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    fontSize: "12px",
    fontWeight: 600,
    fontFamily: fonts.body,
    color: inkColors.ink2,
  },
  rowName: {
    textTransform: "capitalize",
  },
  rowCheck: {
    color: brandColors.primary,
    fontWeight: 700,
  },
  rowModels: {
    fontSize: "10px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
});

export const BackendControl: React.FC = () => {
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<BackendsState | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const load = useCallback(() => {
    getBackends()
      .then((next) => {
        setState(next);
        setError(null);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : String(err));
      });
  }, []);

  // Fetch once for the closed-state summary; refetch on each open.
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const current = state?.current;

  const select = useCallback(
    async (provider: string) => {
      if (provider === current || busy) return;
      setBusy(provider);
      setError(null);
      try {
        const next = await setBackend(provider);
        setState((prev) => (prev ? { ...prev, current: next.provider } : prev));
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusy(null);
      }
    },
    [current, busy],
  );

  return (
    <div className={styles.root} ref={rootRef}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen((v) => !v)}
        title="Choose the LLM model backend used for your runs, chat, and edits"
        aria-expanded={open}
        aria-haspopup="true"
      >
        <span className={styles.triggerIcon} aria-hidden="true">
          ◈
        </span>
        <span className={styles.triggerLabel}>{current ?? "Model"}</span>
      </button>

      {open && (
        <div className={styles.popover} role="menu" aria-label="Model backend selection">
          <div className={styles.head}>Model backend</div>
          {error && <div className={styles.error}>{error}</div>}
          {!state && !error && <div className={styles.note}>Loading…</div>}
          {state?.backends.map((row) => {
            const selected = row.provider === current;
            return (
              <button
                type="button"
                key={row.provider}
                role="menuitem"
                className={mergeClasses(styles.row, selected && styles.rowSelected)}
                disabled={busy !== null}
                aria-pressed={selected}
                onClick={() => void select(row.provider)}
              >
                <span className={styles.rowHead}>
                  <span className={styles.rowName}>{row.provider}</span>
                  {selected ? (
                    <span className={styles.rowCheck} aria-hidden="true">
                      ✓
                    </span>
                  ) : busy === row.provider ? (
                    <span aria-hidden="true">…</span>
                  ) : null}
                </span>
                <span className={styles.rowModels}>{modelSummary(row)}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BackendControl;
